import { useCallback } from "react";
import { newId } from "./parse";
import { useServerState } from "../utils/useServerState";

// Saved views: a name for a search + filters + sort (the URL query) together
// with a column layout. Saved per user by the API.
//
// A view: { id, name, query, layout: { order, hidden }, createdAt, updatedAt }

// Parameters that belong to a view; anything else in the URL (the open
// channel drawer) is left out
const NOT_VIEW = new Set(["channel"]);

// The query in a fixed order, so the same filters always compare equal
export function viewQuery(params) {
  const pairs = [...params.entries()].filter(([k]) => !NOT_VIEW.has(k));
  pairs.sort(([a, x], [b, y]) => a.localeCompare(b) || x.localeCompare(y));
  return new URLSearchParams(pairs).toString();
}

const sameLayout = (a, b) =>
  JSON.stringify(a?.order) === JSON.stringify(b?.order) &&
  JSON.stringify([...(a?.hidden || [])].sort()) === JSON.stringify([...(b?.hidden || [])].sort());

export function matchesView(view, query, layout) {
  return view.query === query && sameLayout(view.layout, layout);
}

const fromServer = (saved) => (Array.isArray(saved) ? saved : []);

export function useSavedViews(userKey) {
  const [views, setViews] = useServerState("/me/views", userKey, [], fromServer);

  const saveView = useCallback((name, query, layout) => {
    const at = Date.now();
    const view = { id: newId(), name, query, layout, createdAt: at, updatedAt: at };
    setViews((list) => [...list, view]);
    return view;
  }, [setViews]);

  const updateView = useCallback(
    (id, query, layout) =>
      setViews((list) =>
        list.map((v) => (v.id === id ? { ...v, query, layout, updatedAt: Date.now() } : v))
      ),
    [setViews]
  );

  const renameView = useCallback(
    (id, name) => setViews((list) => list.map((v) => (v.id === id ? { ...v, name } : v))),
    [setViews]
  );

  const deleteView = useCallback((id) => setViews((list) => list.filter((v) => v.id !== id)), [setViews]);

  return { views, saveView, updateView, renameView, deleteView };
}
